import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';

/**
 * CardMenu - Reusable kebab menu (⋮) dropdown for card/row actions.
 *
 * Uses a portal to render at document.body level so the dropdown is never
 * clipped by parent overflow-hidden containers or affected by parent opacity.
 * Follows the repo's dropdown convention (fixed backdrop + z-40/z-50 layering)
 * but renders via portal because CardMenu is used inside overflow-hidden table
 * cells and card containers where absolute positioning would be clipped.
 *
 * @param {Object} props
 * @param {Array} props.items - Array of menu item objects:
 *   { label, onClick, icon, iconClass, destructive, disabled, type }
 *   icon: emoji string OR React element (e.g. <Edit2 size={16} />)
 *   iconClass: optional className for the icon wrapper (e.g. 'text-[#52A675]')
 *   type: 'divider' renders a horizontal line (all other fields ignored)
 * @param {string} [props.size='md'] - Trigger button size: 'sm' or 'md'
 *
 * @example
 * <CardMenu items={[
 *   { label: 'Edit', icon: <Edit2 size={16} />, iconClass: 'text-[#52A675]', onClick: handleEdit },
 *   { label: 'Duplicate', icon: <Copy size={16} />, iconClass: 'text-[#52A675]', onClick: handleDuplicate },
 *   { type: 'divider' },
 *   { label: 'Delete', icon: <Trash2 size={16} />, iconClass: 'text-red-500', onClick: handleDelete, destructive: true },
 * ]} />
 */
const CardMenu = ({ items, size = 'md' }) => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });

  // Position the dropdown relative to the trigger button.
  // Flips upward if there isn't enough room below.
  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const menuHeight = 220; // estimated max height of dropdown
    const spaceBelow = window.innerHeight - rect.bottom;
    const openAbove = spaceBelow < menuHeight && rect.top > menuHeight;

    setMenuPos({
      top: openAbove ? rect.top - 4 : rect.bottom + 4,
      left: rect.right,
      above: openAbove,
    });
  }, []);

  // Close on Escape key
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open]);

  // Close on scroll (any ancestor) to prevent floating menu from drifting
  useEffect(() => {
    if (!open) return;
    const handleScroll = () => setOpen(false);
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [open]);

  const handleToggle = (e) => {
    e.stopPropagation();
    if (!open) updatePosition();
    setOpen(prev => !prev);
  };

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        onClick={handleToggle}
        className={`flex items-center justify-center rounded-lg transition-colors text-gray-500 hover:text-[#2F5233] hover:bg-gray-100 ${
          size === 'sm' ? 'w-7 h-7 text-base' : 'w-8 h-8 text-xl'
        }`}
        aria-label="More options"
      >
        ⋮
      </button>

      {open && createPortal(
        <>
          {/* Fixed backdrop to close dropdown when clicking outside (z-40) */}
          <div
            className="fixed inset-0"
            style={{ zIndex: 40 }}
            onClick={() => setOpen(false)}
          />
          {/* Dropdown menu (z-50) */}
          <div
            ref={menuRef}
            className="fixed min-w-[180px] bg-white border border-gray-200 rounded-lg shadow-lg py-1"
            style={{
              zIndex: 50,
              top: menuPos.above ? undefined : menuPos.top,
              bottom: menuPos.above ? (window.innerHeight - menuPos.top) : undefined,
              left: menuPos.left,
              transform: 'translateX(-100%)'
            }}
          >
            {items.map((item, idx) => {
              if (item.type === 'divider') {
                return (
                  <div
                    key={`divider-${idx}`}
                    className="h-px bg-gray-200 my-1.5 mx-2"
                  />
                );
              }

              return (
                <button
                  key={idx}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!item.disabled && item.onClick) {
                      item.onClick();
                    }
                    setOpen(false);
                  }}
                  disabled={item.disabled}
                  className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-left text-sm transition-colors ${
                    item.disabled
                      ? 'text-gray-400 cursor-not-allowed'
                      : item.destructive
                        ? 'text-red-700 hover:bg-red-50'
                        : 'text-[#2F5233] hover:bg-gray-50'
                  }`}
                >
                  {item.icon && (
                    <span className={`w-5 text-center flex-shrink-0 ${item.iconClass || ''}`}>{item.icon}</span>
                  )}
                  <span className="font-medium">{item.label}</span>
                </button>
              );
            })}
          </div>
        </>,
        document.body
      )}
    </div>
  );
};

export default CardMenu;
